import { useMutation, useQuery } from '@tanstack/react-query';
import { Button, Card, Form, Input, message, Select, Tabs, Tag } from 'antd';
import moment from 'moment';
import { useMemo, useState } from 'react';

import { axiosClient } from '@/api/axiosClient';
import { useGetCustomerList } from '@/modules/customer/hooks/useGetCustomerList';
import { useGetHRList } from '@/modules/hr-management/hooks/useGetHRList';
import { useGetRecruitmentTeamsList } from '@/modules/hr-management/hooks/useGetRecruitmentTeamsList';
import { useGetWorkerList } from '@/modules/worker/hooks/useGetWorkerList';

// T-TT-02 V1.4 D3.1 — Admin tạo & gửi thông báo. NLĐ nhận push (FCM) + in-app.
//
// D3.1 replaces the free-text "Target IDs" with an antd Select multiple,
// options sourced from the matching list endpoint per target_type. ALL needs
// no IDs at all; CUSTOMER/TEAM/USER/WORKER each hit their own list hook.
//
// A "Đã gửi" tab calls /notification/sent so admins can audit recent sends —
// this satisfies the second half of the T-TT-02 DoD that was missing in V1.

type TargetType = 'ALL' | 'CUSTOMER' | 'TEAM' | 'USER' | 'WORKER';

interface SentRow {
  id: string;
  title: string;
  body: string;
  category: string;
  target_type: string;
  sent_at: string;
  sent_count?: number;
}

const NotificationPage = () => {
  const [form] = Form.useForm();
  const [targetType, setTargetType] = useState<TargetType>('ALL');

  const sendMutation = useMutation(
    (payload: Record<string, unknown>) => axiosClient.post('/notification/send', payload),
    {
      onSuccess: () => {
        message.success('Đã gửi thông báo');
        form.resetFields();
        setTargetType('ALL');
      },
    }
  );

  // Per-target-type option sources. Enabled flag keeps inactive ones from
  // making pointless API calls.
  const { data: customers } = useGetCustomerList('page=1&limit=200', {
    enabled: targetType === 'CUSTOMER',
  });
  const { data: teams } = useGetRecruitmentTeamsList('page=1&limit=200', {
    enabled: targetType === 'TEAM',
  });
  const { data: users } = useGetHRList('page=1&limit=200', {
    enabled: targetType === 'USER',
  });
  const { data: workers } = useGetWorkerList('page=1&limit=200', {
    enabled: targetType === 'WORKER',
  });

  const targetOptions = useMemo(() => {
    type Row = { id: string; name?: string; full_name?: string; phone?: string };
    const pick = (rows?: Row[]) =>
      (rows ?? []).map((r) => ({
        label:
          (r.full_name || r.name || '(không tên)') + (r.phone ? ' • ' + r.phone : ''),
        value: r.id,
      }));

    switch (targetType) {
      case 'CUSTOMER':
        return pick((customers as unknown as { rows?: Row[] })?.rows);
      case 'TEAM':
        return pick((teams as unknown as { rows?: Row[] })?.rows);
      case 'USER':
        return pick((users as unknown as { rows?: Row[] })?.rows);
      case 'WORKER':
        return pick((workers as unknown as { rows?: Row[] })?.rows);
      default:
        return [];
    }
  }, [targetType, customers, teams, users, workers]);

  // QC #37 — /notification/sent now exists on BE. Admin sees what they've
  // pushed (?all=true to see everyone's). Returns rows + sent_count.
  const { data: sent, isLoading: sentLoading } = useQuery(
    ['notif-sent'],
    () => axiosClient.get<{ rows: SentRow[] }>('/notification/sent?page=1&limit=50'),
    { refetchOnWindowFocus: false }
  );

  async function handleSend() {
    const values = await form.validateFields();
    sendMutation.mutate({
      body: values.body,
      category: values.category || 'GENERAL',
      deep_link: values.deep_link || undefined,
      image_url: values.image_url || undefined,
      target_ids: values.target_ids || [],
      target_type: values.target_type,
      title: values.title,
    });
  }

  return (
    <Card title="Đẩy thông báo NLĐ (T-TT-02)">
      <Tabs
        items={[
          {
            children: (
              <Form form={form} layout="vertical" style={{ maxWidth: 720 }}>
                <Form.Item label="Tiêu đề" name="title" rules={[{ required: true }]}>
                  <Input />
                </Form.Item>
                <Form.Item label="Nội dung" name="body" rules={[{ required: true }]}>
                  <Input.TextArea rows={4} />
                </Form.Item>
                <Form.Item label="Image URL" name="image_url">
                  <Input placeholder="https://..." />
                </Form.Item>
                <Form.Item label="Deep link" name="deep_link">
                  <Input placeholder="app://..." />
                </Form.Item>
                <Form.Item initialValue="GENERAL" label="Danh mục" name="category">
                  <Select
                    options={['GENERAL', 'PAYROLL', 'LEAVE', 'VIOLATION', 'SURVEY'].map(
                      (v) => ({ label: v, value: v })
                    )}
                  />
                </Form.Item>
                <Form.Item
                  extra="ALL = tất cả NLĐ (trừ blacklist). Các loại khác chọn ID từ danh sách bên dưới."
                  label="Đối tượng"
                  name="target_type"
                  rules={[{ required: true }]}
                >
                  <Select
                    onChange={(v: TargetType) => {
                      setTargetType(v);
                      form.setFieldValue('target_ids', []);
                    }}
                    options={['ALL', 'CUSTOMER', 'TEAM', 'USER', 'WORKER'].map((v) => ({
                      label: v,
                      value: v,
                    }))}
                  />
                </Form.Item>
                {targetType !== 'ALL' && (
                  <Form.Item
                    extra="Chọn nhiều đối tượng cùng lúc."
                    label={`Chọn ${targetType.toLowerCase()}`}
                    name="target_ids"
                    rules={[{ message: 'Chọn ít nhất 1 đối tượng', required: true }]}
                  >
                    <Select
                      mode="multiple"
                      optionFilterProp="label"
                      options={targetOptions}
                      placeholder={`Tìm ${targetType.toLowerCase()}...`}
                      showSearch
                    />
                  </Form.Item>
                )}
                <Button
                  loading={sendMutation.isLoading}
                  onClick={handleSend}
                  type="primary"
                >
                  Gửi thông báo
                </Button>
              </Form>
            ),
            key: 'send',
            label: 'Soạn thông báo',
          },
          {
            children: (
              <div style={{ maxWidth: 960 }}>
                {sentLoading && <div>Loading...</div>}
                {!sentLoading &&
                  ((sent as unknown as { data?: { rows?: SentRow[] } })?.data?.rows ?? []).map(
                    (row) => (
                    <Card
                      key={row.id}
                      size="small"
                      style={{ marginBottom: 8 }}
                      title={
                        <span>
                          <Tag color="blue">{row.category}</Tag>
                          {row.title}
                          <span style={{ color: '#999', float: 'right', fontSize: 12 }}>
                            {moment(row.sent_at).format('DD/MM HH:mm')} •{' '}
                            {row.sent_count ?? 0} NLĐ • {row.target_type}
                          </span>
                        </span>
                      }
                    >
                      <p style={{ margin: 0 }}>{row.body}</p>
                    </Card>
                    )
                  )}
              </div>
            ),
            key: 'sent',
            label: 'Đã gửi',
          },
        ]}
      />
    </Card>
  );
};

export default NotificationPage;
